import React from 'react';
import {parseDate} from './util';

const {
    hoc: {withNoProps},
    hooks: {useSelector, useHover},
    components: {generic: {RawHTML}, frontend = {}},
    css: {prefixCSS, measurementToString},
    icons: {FacebookIcon, TwitterIcon, InstagramIcon, EmailIcon, LinkedinIcon}
} = polyphony;

const formatDates = (start, end) => {
	start = start ? parseDate(start) : null;
	end = end ? parseDate(end) : null;

	const dateTimeFormat = new Intl.DateTimeFormat('en', {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
		hour: "numeric",
		minute: "numeric"
	});

	// TODO: make sure the time zone matches WordPress

	if (start && end) {
		return dateTimeFormat.formatRange(start, end);
	}

	if (start) {
		return dateTimeFormat.format(start);
	}

	if (end) {
		return dateTimeFormat.format(end);
	}

	return '';
};

const formatDate = d => {
    if (!d) return d;
    return parseDate(d).toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric'
    });
};

const useMeasurement = m => useSelector(s => s.measurements[m]);

const styles = {
    sharebuttons: prefixCSS({
        display: 'flex',
        flexFlow: 'row wrap',
        marginTop: 'auto',
        padding: '20px 5px'
    })
};

const SharingButton = ({href, icon: Icon, hoverColor = 'lightgray', style}) => {
    const [{hover}, ref] = useHover();
    const size = 33;

    return <a ref={ref} style={prefixCSS({margin: '5px 15px 5px 0px',
                                          padding: '5px 5px 5px 5px',
                                          display: 'flex',
                                          justifyContent: 'center',
                                          alignItems: 'center',
                                          cursor: 'pointer',
                                          fontSize: size,
                                          boxSizing: 'border-box', ...style})} href={href} rel="nofollow noopener" target="_blank">
               <Icon style={hover ? {color: hoverColor} : undefined} height={size} />
               <svg style={hover ? {color: hoverColor, paddingLeft: 5} : {paddingLeft: 5}} key="svg" height={size} viewBox="0 0 256 512"><path fill="currentColor" d="M24.707 38.101L4.908 57.899c-4.686 4.686-4.686 12.284 0 16.971L185.607 256 4.908 437.13c-4.686 4.686-4.686 12.284 0 16.971L24.707 473.9c4.686 4.686 12.284 4.686 16.971 0l209.414-209.414c4.686-4.686 4.686-12.284 0-16.971L41.678 38.101c-4.687-4.687-12.285-4.687-16.971 0z" /></svg>
           </a>;
};

// Component for ALL posts
const SICPost = withNoProps(() => {
    const {PostWrapper, PostBody} = frontend;

    const post = useSelector(s => s.posts.length > 0 ? s.posts[0] : null, (a, b) => a.ID === b.ID);
    const isHome = useSelector(s => s.is.front_page);
    const linkColor = useSelector(s => s.colors.link_color);
    const linkColorHover = useSelector(s => s.colors.link_color_hover);
    const bodyFontSize = useMeasurement('post_body_font_size');
    const titleFontSize = useMeasurement('post_title_font_size');
    const paragraphSpace = useMeasurement('paragraph_spacing');
    const titleSpaceBelow = useMeasurement('post_title_bottom_margin');
    const taglineMargin = useMeasurement('tagline_top_margin');
    const taglineFontSize = useMeasurement('tagline_font_size');
    const headerTopMargin = useMeasurement('header_tag_top_margin');
    const headerBottomMargin = useMeasurement('header_tag_bottom_margin');
    const webpageTitle = useSelector(s => encodeURIComponent(s.webpage_title));
    const siteName = useSelector(s => encodeURIComponent(s.site_name));
    const url = encodeURIComponent(location.href);

    const {content: bodies, title, post_type, meta, post_date_iso} = post;

    const isEvent = post_type.name === 'event';
    const isPage = post_type.name === 'page';

    let tagline = null;

    if (isEvent) {
        const {event_city, event_country, event_state, event_street, event_zip,
               event_start_timestamp, event_end_timestamp, event_location_name} = meta;
        tagline = formatDates(event_start_timestamp, event_end_timestamp);
        let addr = [event_street, event_city, event_state, event_zip, event_country].filter(Boolean).join(', ');
        tagline = <>{tagline}{event_location_name && <><br />{event_location_name}</>}<br />{addr}</>;
    } else if (!isPage) {
        tagline = formatDate(post_date_iso);
    }

    const css = `
.sicpost-body p:not(:last-of-type) {margin-bottom: ${paragraphSpace}}
.sicpost-body h1,.sicpost-body h2,.sicpost-body h3,.sicpost-body h4,.sicpost-body h5,.sicpost-body h6 {margin-top: ${headerTopMargin};margin-bottom: ${headerBottomMargin};}
.sicpost-body p a {color: ${linkColor} !important}
.sicpost-body p a:hover {color: ${linkColorHover} !important}`;

    const taglineStyle = {marginTop: taglineMargin, marginBottom: `calc(${measurementToString(taglineMargin)} + ${measurementToString(titleSpaceBelow)})`, fontSize: taglineFontSize};

    return [
        <style key="style">{css}</style>,
        <PostWrapper key="postwrapper" post={post} className="sicpost">
            {!isHome && <>
            <h1 key="title" style={{fontSize: titleFontSize}}>
                <RawHTML>{title}</RawHTML>
            </h1>
            <h2 key="tagline" style={taglineStyle}>{tagline}</h2>
            </>}
            {bodies.map((x, i) =>
            <div role="none" className='sicpost-body' key={`${x.ID} ${i}`} style={{fontSize: bodyFontSize}}>
                <PostBody>{x}</PostBody>
            </div>)}
            {!isHome && <div role="none" key="sharebuttons" style={styles.sharebuttons}>
                {isEvent && <SharingButton icon={({style}) => <span style={{...style, fontSize: 'inherit'}}>TICKETS</span>} key='tickets' href={meta.event_tickets_url || '#'} />}
                <SharingButton href={`https://www.facebook.com/sharer/sharer.php?u=${url}`} icon={FacebookIcon} hoverColor="#3b5998" />
                <SharingButton href={`https://twitter.com/intent/tweet?text=${url}`} icon={TwitterIcon} hoverColor="#00ACED" />
                {false && <SharingButton href={`#`} icon={InstagramIcon} hoverColor="#833AB4" />}
                <SharingButton icon={LinkedinIcon} href={`https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${webpageTitle}&source=${siteName}`} hoverColor="#0077B5" />
                <SharingButton icon={EmailIcon} href={`mailto:?body=${url}`} hoverColor="lightgray" />
            </div>}
        </PostWrapper>
    ];
});

SICPost.displayName = 'SICPost';

export default SICPost;

